import type { EquipmentKind } from '@cadencia/contracts';
import { EXERCISES, requireExercise, type Exercise } from '../catalog/exercises.js';
import type { Muscle } from '../catalog/muscles.js';
import { classifyChange, type ChangeClassification } from '../decisions/change-policy.js';
import { clamp } from '../units/round.js';
import type { VolumeStatus } from './volume.js';

/**
 * Sustitucion de ejercicios (docs/TRAINING_ENGINE.md §7.2).
 *
 * La frase que define el modulo esta en el propio documento: "nunca es una
 * simple coincidencia de grupo muscular". Por eso los musculos compartidos solo
 * pueden aportar 0,40 de 1,00: un candidato que trabaje exactamente lo mismo
 * pero con otro patron, otro perfil de resistencia y otro rol no llega al
 * umbral de equivalencia por mucho que el musculo coincida.
 */

/** Pesos de §7.2. Suman exactamente 1,00. */
export const SUBSTITUTION_WEIGHTS = {
  primaryMuscles: 0.25,
  secondaryMuscles: 0.15,
  pattern: 0.2,
  resistanceProfile: 0.15,
  stretchEmphasis: 0.1,
  role: 0.1,
  availability: 0.05,
} as const;

/** Penalizaciones de §7.2. CONVENCION declarada: el documento no da cifras. */
export const SUBSTITUTION_PENALTIES = {
  volumeOverTarget: 0.075,
  volumeAboveMax: 0.15,
  /** Por punto de demanda tecnica por encima de 2, solo si el readiness es bajo. */
  technicalDemandPerPoint: 0.03,
  lowReadinessScore: 50,
} as const;

/**
 * Puntuacion minima para ofrecer un candidato.
 *
 * CONVENCION declarada. Es el mecanismo que hace cierto CA-3.4 —"ninguna es una
 * coincidencia trivial de grupo muscular"— tambien en un gimnasio mal equipado:
 * si no hay tres alternativas decentes se devuelven menos, no se rellena con
 * ejercicios malos. Tres opciones pobres presentadas como buenas son peores que
 * una sola honesta.
 */
export const MIN_SUBSTITUTION_SCORE = 0.35;

// ---------------------------------------------------------------------------
// Componentes
// ---------------------------------------------------------------------------

/**
 * Solape entre dos conjuntos de musculos (Jaccard).
 *
 * Simetrico a proposito: penaliza tanto no cubrir lo que cubria el original
 * como arrastrar musculos que el original no tocaba. Una sustitucion no deberia
 * cambiar el reparto de volumen de la semana.
 */
export function muscleOverlap(a: readonly Muscle[], b: readonly Muscle[]): number {
  if (a.length === 0 && b.length === 0) return 1; // nada que compartir, nada que diferir
  const conjuntoA = new Set(a);
  const conjuntoB = new Set(b);
  let comunes = 0;
  for (const musculo of conjuntoA) if (conjuntoB.has(musculo)) comunes += 1;
  const union = new Set([...a, ...b]).size;
  return union === 0 ? 1 : comunes / union;
}

/** Perfiles opuestos: lo que sube de resistencia frente a lo que baja. */
const OPUESTOS: ReadonlySet<string> = new Set(['ascending|descending', 'descending|ascending']);

export function resistanceCompatibility(a: Exercise, b: Exercise): number {
  if (a.resistanceProfile === b.resistanceProfile) return 1;
  if (OPUESTOS.has(`${a.resistanceProfile}|${b.resistanceProfile}`)) return 0;
  // Todo lo demas es parcialmente compatible: `flat`, `bell` y `variable` no
  // contradicen a nada, solo dejan de coincidir.
  return 0.5;
}

const ORDEN_CURVA: Record<string, number> = { lengthened: 0, mid: 1, shortened: 2 };

export function stretchCompatibility(a: Exercise, b: Exercise): number {
  const distancia = Math.abs(
    (ORDEN_CURVA[a.stretchEmphasis] ?? 1) - (ORDEN_CURVA[b.stretchEmphasis] ?? 1),
  );
  if (distancia === 0) return 1;
  if (distancia === 1) return 0.5;
  return 0; // acortado frente a estirado: no es la misma region de la curva
}

/**
 * Un compuesto sustituye a un compuesto y un aislamiento a un aislamiento.
 *
 * Es la aproximacion del "mismo rol" del documento: el rol lo lleva la sesion,
 * no el ejercicio, pero lo que hace que un ejercicio pueda ocupar el hueco de
 * otro es si carga como principal o como accesorio.
 */
export function roleCompatibility(a: Exercise, b: Exercise): number {
  return a.isCompound === b.isCompound ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Contexto y puntuacion
// ---------------------------------------------------------------------------

export interface SubstitutionContext {
  /** Tipos de equipamiento del gimnasio. Filtro duro (§7.1). */
  availableKinds: readonly EquipmentKind[];
  /** Ejercicios que el usuario ha bloqueado. Filtro duro. */
  blockedSlugs?: readonly string[];
  /** Ejercicios cuya maquina esta libre ahora mismo. */
  freeNowSlugs?: readonly string[];
  /** Situacion del volumen semanal por musculo, de `volumeReport`. */
  weeklyVolume?: Partial<Record<Muscle, VolumeStatus>>;
  /** Readiness de hoy, 0-100. */
  readinessScore?: number;
}

export interface SubstitutionScore {
  score: number;
  breakdown: Record<string, number>;
  reasons: string[];
}

export function scoreSubstitution(
  original: Exercise,
  candidate: Exercise,
  context: SubstitutionContext,
): SubstitutionScore {
  const w = SUBSTITUTION_WEIGHTS;

  const primarios = muscleOverlap(original.primaryMuscles, candidate.primaryMuscles);
  const secundarios = muscleOverlap(original.secondaryMuscles, candidate.secondaryMuscles);
  const patron = original.pattern === candidate.pattern ? 1 : 0;
  const resistencia = resistanceCompatibility(original, candidate);
  const curva = stretchCompatibility(original, candidate);
  const rol = roleCompatibility(original, candidate);
  const libre = (context.freeNowSlugs ?? []).includes(candidate.slug) ? 1 : 0;

  const breakdown: Record<string, number> = {
    primaryMuscles: primarios * w.primaryMuscles,
    secondaryMuscles: secundarios * w.secondaryMuscles,
    pattern: patron * w.pattern,
    resistanceProfile: resistencia * w.resistanceProfile,
    stretchEmphasis: curva * w.stretchEmphasis,
    role: rol * w.role,
    availability: libre * w.availability,
  };

  // Penalizacion por volumen ya acumulado en la region esta semana.
  let penalizacionVolumen = 0;
  for (const musculo of candidate.primaryMuscles) {
    const situacion = context.weeklyVolume?.[musculo];
    if (situacion === 'above_max') {
      penalizacionVolumen = Math.max(penalizacionVolumen, SUBSTITUTION_PENALTIES.volumeAboveMax);
    } else if (situacion === 'over_target') {
      penalizacionVolumen = Math.max(penalizacionVolumen, SUBSTITUTION_PENALTIES.volumeOverTarget);
    }
  }
  // `0 - x` y no `-x`: negar el cero da -0, que no es igual a 0 segun
  // `Object.is` y haria fallar cualquier comparacion posterior del desglose.
  breakdown.volumePenalty = 0 - penalizacionVolumen;

  // Penalizacion por demanda tecnica cuando el readiness es bajo: un dia malo
  // no es el dia de estrenar un ejercicio dificil.
  let penalizacionTecnica = 0;
  const readiness = context.readinessScore;
  if (readiness !== undefined && readiness < SUBSTITUTION_PENALTIES.lowReadinessScore) {
    penalizacionTecnica =
      Math.max(0, candidate.technicalDemand - 2) * SUBSTITUTION_PENALTIES.technicalDemandPerPoint;
  }
  breakdown.technicalPenalty = 0 - penalizacionTecnica;

  const bruto = Object.values(breakdown).reduce((suma, valor) => suma + valor, 0);
  const score = clamp(Math.round(bruto * 1000) / 1000, 0, 1);

  return { score, breakdown, reasons: buildReasons(original, candidate, { patron, libre }) };
}

function buildReasons(
  original: Exercise,
  candidate: Exercise,
  flags: { patron: number; libre: number },
): string[] {
  const motivos: string[] = [];

  if (flags.patron === 1) motivos.push('mismo patrón de movimiento');
  if (muscleOverlap(original.primaryMuscles, candidate.primaryMuscles) === 1) {
    motivos.push('mismos músculos principales');
  }
  if (candidate.technicalDemand < original.technicalDemand) motivos.push('menos exigente técnicamente');
  if (candidate.axialLoad < original.axialLoad) motivos.push('menos carga lumbar');
  if (candidate.isUnilateral && !original.isUnilateral) motivos.push('unilateral');
  if (candidate.stretchEmphasis === 'lengthened' && original.stretchEmphasis !== 'lengthened') {
    motivos.push('más recorrido en estiramiento');
  }
  if (flags.libre === 1) motivos.push('equipamiento libre ahora');

  return motivos;
}

// ---------------------------------------------------------------------------
// Busqueda
// ---------------------------------------------------------------------------

export interface SubstitutionCandidate {
  slug: string;
  nameEs: string;
  score: number;
  /** Motivos concretos, ya redactados. */
  reasons: string[];
  /** Una linea para la tarjeta. */
  reason: string;
  /** Menor si el estimulo es equivalente; relevante si lo cambia. */
  change: ChangeClassification;
  breakdown: Record<string, number>;
}

/**
 * Las mejores alternativas a un ejercicio, ordenadas.
 *
 * Filtros duros antes de puntuar (§7.1): equipamiento del gimnasio, ejercicios
 * bloqueados y el propio ejercicio. Puntuar lo que no se puede hacer solo
 * gastaria tiempo y podria colar en el podio algo inservible.
 */
export function findSubstitutions(
  originalSlug: string,
  context: SubstitutionContext,
  limit = 3,
): SubstitutionCandidate[] {
  const original = requireExercise(originalSlug);
  const equipos = new Set(context.availableKinds);
  const bloqueados = new Set(context.blockedSlugs ?? []);

  return EXERCISES.filter(
    (candidato) =>
      candidato.slug !== original.slug &&
      !bloqueados.has(candidato.slug) &&
      equipos.has(candidato.equipmentKind),
  )
    .map((candidato) => {
      const { score, breakdown, reasons } = scoreSubstitution(original, candidato, context);
      return {
        slug: candidato.slug,
        nameEs: candidato.nameEs,
        score,
        reasons,
        reason: reasons.length > 0 ? reasons.join(', ') : 'alternativa disponible',
        change: classifyChange({ kind: 'equivalent_substitution', similarity: score }),
        breakdown,
      };
    })
    .filter((candidato) => candidato.score >= MIN_SUBSTITUTION_SCORE)
    .sort((a, b) => (b.score !== a.score ? b.score - a.score : a.slug.localeCompare(b.slug)))
    .slice(0, limit);
}
